/**
 * 品目欄をクリックしたときに出す製品リスト。
 * 品目欄に入力した文字でリストを絞り込み、クリックした製品を行に入れる。
 */
import { formatYen } from './model';

import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import type { CatalogItemRecord, ProductCatalogRecord } from '@/hooks/useProductCatalogs';

interface Props {
  catalogs: ProductCatalogRecord[];
  selectedCatalogId: string | null;
  onSelectCatalog: (id: string) => void;
  items: CatalogItemRecord[];
  isLoading: boolean;
  noActiveCatalog: boolean;
  /** 主軸カテゴリでカタログを絞り込めるとき (linkedBrandNames あり) だけ「全商品表示」を出す */
  primaryCategoryName: string | null;
  isFiltered: boolean;
  showAllCatalogs: boolean;
  onToggleShowAll: () => void;
  /** 品目欄に入力中の文字 */
  query: string;
  /** 行の品目名と同じなら絞り込まない (行を開いた直後は全件を見せる) */
  currentName: string;
  currentItemId: string | null;
  onPick: (item: CatalogItemRecord) => void;
  /** 入力した品目名を製品リストにない品目として使う */
  onUseCustom: () => void;
}

/** 現在のコードと同じく 商品名 / 型番 / SKU / 製品カテゴリ / 説明 で検索する。 */
export function filterCatalogItems(
  items: CatalogItemRecord[],
  keyword: string,
): CatalogItemRecord[] {
  const k = keyword.trim().toLowerCase();
  if (!k) return items;
  return items.filter(
    (it) =>
      it.name.toLowerCase().includes(k) ||
      it.modelName.toLowerCase().includes(k) ||
      it.sku.toLowerCase().includes(k) ||
      (it.productCategory ?? '').toLowerCase().includes(k) ||
      (it.description ?? '').toLowerCase().includes(k),
  );
}

function highlight(text: string, q: string) {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="bg-[#ffe08a] text-[#1b2430] rounded-sm px-px">
        {text.slice(i, i + q.length)}
      </mark>
      {text.slice(i + q.length)}
    </>
  );
}

export function ProductPanel({
  catalogs,
  selectedCatalogId,
  onSelectCatalog,
  items,
  isLoading,
  noActiveCatalog,
  primaryCategoryName,
  isFiltered,
  showAllCatalogs,
  onToggleShowAll,
  query,
  currentName,
  currentItemId,
  onPick,
  onUseCustom,
}: Props) {
  const raw = query.trim();
  const filtering = !!raw && !(currentItemId && raw === currentName.trim());
  const list = filtering ? filterCatalogItems(items, raw) : items;
  const exact = items.some((it) => it.name === raw);

  return (
    <>
      <p className="text-xs text-[var(--text-muted)]">
        品目欄に入力すると、このリストを絞り込みます。製品をクリックすると、数量
        1・単価・金額が入ります。リストにない品目名もそのまま使えます。
      </p>

      {primaryCategoryName && (
        <div className="flex items-center justify-between gap-2 flex-wrap px-3 py-2 rounded-md bg-[var(--bg-elevated)] border border-[var(--border)] text-xs">
          <span className="text-[var(--text-secondary)]">
            {isFiltered
              ? `主軸カテゴリ「${primaryCategoryName}」のカタログのみ表示中`
              : '全カタログ表示中'}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={showAllCatalogs}
            onClick={onToggleShowAll}
            className="inline-flex items-center gap-2 text-[var(--text-primary)]"
          >
            <span
              className={`relative w-8 h-[18px] rounded-full transition-colors ${
                showAllCatalogs ? 'bg-[var(--accent)]' : 'bg-[var(--bg-hover)]'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 size-3.5 rounded-full bg-white transition-transform ${
                  showAllCatalogs ? 'translate-x-3.5' : ''
                }`}
              />
            </span>
            全商品表示
          </button>
        </div>
      )}

      {catalogs.length > 1 && (
        <div className="flex items-center gap-1 flex-wrap">
          {catalogs.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelectCatalog(c.id)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                selectedCatalogId === c.id
                  ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                  : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              {c.category}
              <span className="text-[10px] text-[var(--text-muted)] ml-1">
                ({c.label || c.version})
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="text-xs text-[var(--text-muted)]">
        {filtering ? `「${raw}」で絞り込み: ${list.length} 件` : `製品リスト: ${list.length} 件`}
      </div>

      <div className="rounded-md border border-[var(--border)] overflow-hidden">
        {filtering && !exact && (
          <button
            type="button"
            onClick={onUseCustom}
            className="w-full text-left px-3 py-2.5 border-b border-[var(--border)] bg-[var(--accent-amber-subtle)] hover:brightness-105"
          >
            <div className="text-[13px] font-medium text-[var(--text-primary)] break-all">
              「{raw}」を製品リストにない品目として使う
            </div>
            <div className="text-[11px] text-[var(--text-muted)]">
              数量・単価（税抜）は手入力。Enter でも確定できます
            </div>
          </button>
        )}
        {noActiveCatalog ? (
          <p className="px-3 py-4 text-xs text-[var(--text-muted)]">
            適用中の商品カタログがありません。商品マスター画面で Excel/CSV を取込してください。
          </p>
        ) : isLoading ? (
          <div className="py-4">
            <Spinner />
          </div>
        ) : list.length === 0 ? (
          <p className="px-3 py-4 text-xs text-[var(--text-muted)]">該当する製品がありません</p>
        ) : (
          list.map((it) => {
            const isCurrent = currentItemId === it.id;
            return (
              <button
                key={it.id}
                type="button"
                aria-current={isCurrent || undefined}
                onClick={() => onPick(it)}
                className={`w-full grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-left px-3 py-2.5 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] ${
                  isCurrent ? 'bg-[var(--accent-subtle)] shadow-[inset_3px_0_0_var(--accent)]' : ''
                }`}
              >
                <span className="text-[13px] font-medium text-[var(--text-primary)] min-w-0 break-words">
                  {highlight(it.name, filtering ? raw : '')}
                  {it.productCategory && (
                    <Badge variant="default" className="ml-1.5 align-middle">
                      {it.productCategory}
                    </Badge>
                  )}
                </span>
                <span className="text-[13px] tabular-nums text-right text-[var(--text-primary)] whitespace-nowrap">
                  {formatYen(it.priceIncludingTax)}
                </span>
                <span className="text-[11px] text-[var(--text-muted)] min-w-0 truncate">
                  {[it.modelName, it.sku].filter(Boolean).join(' / ')}
                </span>
                <span className="text-[11px] text-[var(--text-muted)] tabular-nums text-right whitespace-nowrap">
                  税抜 {formatYen(it.priceExcludingTax)}
                </span>
              </button>
            );
          })
        )}
      </div>
    </>
  );
}
